"use client";

/**
 * First-party web analytics beacon (ENG-4281).
 *
 * Replaces `@vercel/analytics` on all three sites: it writes both the
 * automatic pageview and the explicit conversions that used to ride Vercel's
 * `track()`. Everything it sends goes to `/api/ingest/web-events` on
 * platform-app, which owns validation, bot filtering, device enrichment, and
 * the `site` / `environment` / `user_id` derivations.
 *
 * Consent gates it exactly as it gated Vercel Analytics: nothing is sent
 * before `measurement` consent, and withdrawing consent stops future capture.
 * Unlike the Vercel SDK there is no injected script to keep alive afterwards,
 * so the gate is a plain conditional rather than a `beforeSend` guard.
 *
 * Default events (ENG-5006 / ENG-5005): while measurement consent is active,
 * leaving a page sends a catalogued `pageleave` that pairs with that pageview
 * via `pageview_id`, and click/change/submit on interactive elements sends
 * one catalogued `element_interaction` with element kind and bounded
 * metadata only — never input values, keystrokes, or field content.
 * Consent-gated exactly like pageviews.
 *
 * Accepted loss (recorded on ENG-4281): no batching and no retry. A beacon
 * that fails is a lost pageview, pageleave, or interaction.
 */

import { usePathname } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";
import { useHasTelemetryConsent } from "./consent/use-telemetry-consent";
import {
  type AnalyticsEventProperties,
  TelemetryEvent,
  validateAnalyticsEventProperties,
  type WebConversionEventName,
} from "./event-catalog";
import { ensureFirstTouchCookie } from "./first-touch";
import { type SyntheticSourceId, WEB_CAPTURE_HEARTBEAT_UTM_CAMPAIGN } from "./synthetic-sources";
import { elementInteractionFromEvent, pageleaveProperties } from "./web-default-capture";

export const WEB_EVENTS_PATH = "/api/ingest/web-events";

/**
 * Session-scoped synthetic marker (ENG-5495). A Datadog heartbeat lands with
 * `utm_campaign=axp_web_capture_heartbeat` on its first page; only that
 * pageview carries the campaign, but its pageleave and element interactions
 * must be marked too, and they may arrive before the pageview or be retried.
 * The marker is remembered for the tab session so every event of that session
 * carries it explicitly and ingest can divert each one on its own.
 */
export const SYNTHETIC_SESSION_STORAGE_KEY = "axp_synthetic_source";

const DEFAULT_ROOT_DOMAIN = "514.ax";
const LOCAL_PLATFORM_APP_PORT = "3001";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

type WebEvent = Record<string, unknown> & { event_id: string; type: "pageview" | "conversion" };

export interface WebAnalyticsBeaconProps {
  /**
   * Set on platform-app, which serves the ingest route itself. A relative URL
   * keeps the request same-origin so the Clerk session cookie rides along and
   * the route can stamp `user_id`; it also keeps previews posting to their own
   * deployment instead of production.
   */
  sameOrigin?: boolean;
}

/**
 * Resolves where the beacon posts. Marketing and docs are on different hosts
 * than the ingest route, so they post cross-origin and the route answers with
 * CORS headers. Going direct rather than proxying through a rewrite is
 * deliberate: a rewrite would present Vercel's own infrastructure as the
 * client, and the `/api/ingest/` edge rate-limit rule (ENG-4261) keys on IP.
 */
export function resolveWebEventsEndpoint(sameOrigin = false): string {
  const configured = process.env.NEXT_PUBLIC_WEB_EVENTS_ENDPOINT?.trim();
  if (configured) return configured;
  if (sameOrigin) return WEB_EVENTS_PATH;

  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//${hostname}:${LOCAL_PLATFORM_APP_PORT}${WEB_EVENTS_PATH}`;
  }

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN?.trim() || DEFAULT_ROOT_DOMAIN;
  return `https://app.${rootDomain}${WEB_EVENTS_PATH}`;
}

/**
 * The registered synthetic source this tab session declared, if any. Reads
 * the current URL first so a fresh heartbeat load marks itself even when
 * sessionStorage is unavailable; then the remembered marker.
 */
export function currentSyntheticSource(): SyntheticSourceId | null {
  if (typeof window === "undefined") return null;
  const campaign = new URLSearchParams(window.location.search).get("utm_campaign");
  if (campaign === WEB_CAPTURE_HEARTBEAT_UTM_CAMPAIGN) {
    rememberSyntheticSource("web_capture_heartbeat");
    return "web_capture_heartbeat";
  }
  try {
    const remembered = window.sessionStorage.getItem(SYNTHETIC_SESSION_STORAGE_KEY);
    return remembered === "web_capture_heartbeat" ? remembered : null;
  } catch {
    return null;
  }
}

function rememberSyntheticSource(source: SyntheticSourceId): void {
  try {
    window.sessionStorage.setItem(SYNTHETIC_SESSION_STORAGE_KEY, source);
  } catch {
    // Storage may be unavailable (privacy mode); the URL still marks this page.
  }
}

/**
 * Traffic markers the browser can state about itself (ENG-5507). Only
 * `navigator.webdriver` — a standard flag automation sets — is passed through;
 * nothing else is inferred. Omitted when the browser does not expose it, so
 * the row stays unknown rather than false.
 */
function browserMarkers(): { synthetic_source?: SyntheticSourceId; webdriver?: true } {
  const markers: { synthetic_source?: SyntheticSourceId; webdriver?: true } = {};
  const synthetic = currentSyntheticSource();
  if (synthetic) markers.synthetic_source = synthetic;
  if (typeof navigator !== "undefined" && navigator.webdriver === true) markers.webdriver = true;
  return markers;
}

/**
 * Fire-and-forget delivery. `sendBeacon` survives the page unload that ends
 * most sessions; the `fetch` fallback keeps the same shape where it is
 * unavailable. Both send `text/plain` so the request stays CORS-simple and
 * never costs a preflight round trip.
 */
function sendWebEvent(event: WebEvent, endpoint: string): void {
  // Independent docs/marketing deployments can precede ingest. Its older
  // strict body schema rejects marker fields, but safely ignores URL metadata.
  // Each request contains one event, so these declarations apply to that event.
  const { synthetic_source, webdriver, ...legacyEvent } = event;
  if (synthetic_source !== undefined || webdriver !== undefined) {
    const url = new URL(endpoint, window.location.href);
    if (synthetic_source !== undefined)
      url.searchParams.set("synthetic_source", String(synthetic_source));
    if (webdriver !== undefined) url.searchParams.set("webdriver", String(webdriver));
    endpoint = url.href;
  }
  const body = JSON.stringify({ events: [legacyEvent] });

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
    if (navigator.sendBeacon(endpoint, blob)) return;
  }

  void fetch(endpoint, {
    method: "POST",
    keepalive: true,
    headers: { "content-type": "text/plain;charset=UTF-8" },
    body,
  }).catch(() => {
    // A lost beacon is an accepted loss; it must never surface to the visitor.
  });
}

/**
 * Records a conversion. Replaces `track()` from `@vercel/analytics`, which
 * carried the same `cid` from the ax_ft first-touch cookie.
 */
export function trackWebConversion<Name extends WebConversionEventName>(
  eventName: Name,
  hasTelemetryConsent: boolean,
  properties?: AnalyticsEventProperties<Name>,
  options: { sameOrigin?: boolean } = {},
): void {
  if (!hasTelemetryConsent || typeof window === "undefined") return;

  try {
    validateAnalyticsEventProperties(eventName, properties ?? {});
  } catch {
    console.error("Telemetry capture validation failed", { eventName });
    return;
  }
  const firstTouch = ensureFirstTouchCookie(hasTelemetryConsent);
  sendWebEvent(
    {
      event_id: crypto.randomUUID(),
      type: "conversion",
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      event_name: eventName,
      cid: firstTouch?.cid ?? null,
      ...(properties === undefined ? {} : { properties }),
      ...browserMarkers(),
    },
    resolveWebEventsEndpoint(options.sameOrigin),
  );
}

export function WebAnalyticsBeacon({ sameOrigin }: WebAnalyticsBeaconProps) {
  return (
    <Suspense fallback={null}>
      <PageviewBeacon sameOrigin={sameOrigin} />
    </Suspense>
  );
}

function PageviewBeacon({ sameOrigin }: WebAnalyticsBeaconProps) {
  const hasTelemetryConsent = useHasTelemetryConsent();
  const pathname = usePathname();
  // Read during render so an effect cleanup that runs after a consent
  // withdrawal sees the new value and skips the pageleave.
  const consentRef = useRef(hasTelemetryConsent);
  consentRef.current = hasTelemetryConsent;

  useEffect(() => {
    // Re-runs on withdrawal and returns, so no further pageview is captured.
    if (!hasTelemetryConsent) return;

    const firstTouch = ensureFirstTouchCookie(hasTelemetryConsent);
    const cid = firstTouch?.cid ?? null;
    const pageviewId = crypto.randomUUID();
    const endpoint = resolveWebEventsEndpoint(sameOrigin);
    // Capture the path this effect owns. On SPA navigation the cleanup runs
    // after the next render, when window.location may already be the new
    // route; pairing must still name the pageview that is leaving.
    const path = pathname;
    // Only the five UTM keys, never the whole query string: page URLs carry
    // invite tokens and one-time links.
    const searchParams = new URLSearchParams(window.location.search);
    const utm = Object.fromEntries(
      UTM_KEYS.map((key) => [key, searchParams.get(key)]).filter(([, value]) => value !== null),
    );

    sendWebEvent(
      {
        event_id: pageviewId,
        type: "pageview",
        timestamp: new Date().toISOString(),
        path,
        referrer: document.referrer || null,
        cid,
        ...utm,
        ...browserMarkers(),
      },
      endpoint,
    );

    let left = false;
    const sendPageleave = () => {
      if (left) return;
      left = true;
      sendCataloguedWebEvent(TelemetryEvent.PAGELEAVE, pageleaveProperties(pageviewId), {
        path,
        cid,
        endpoint,
      });
    };

    // pagehide covers tab close and full navigations. A persisted pagehide is
    // a BFCache freeze, not a leave — emitting here would latch `left` and
    // skip the real close after pageshow.persisted restores the same effect.
    const onPageHide = (event: Event) => {
      if (isPersistedPageTransition(event)) return;
      sendPageleave();
    };
    const onPageShow = (event: Event) => {
      if (isPersistedPageTransition(event)) left = false;
    };

    // visibilitychange is not used: hiding a tab is not leaving the page.
    // React cleanup covers client-side route changes.
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      if (!consentRef.current) return;
      sendPageleave();
    };
  }, [hasTelemetryConsent, pathname, sameOrigin]);

  useEffect(() => {
    if (!hasTelemetryConsent) return;

    const firstTouch = ensureFirstTouchCookie(hasTelemetryConsent);
    const cid = firstTouch?.cid ?? null;
    const endpoint = resolveWebEventsEndpoint(sameOrigin);

    const captureInteraction = (event: Event) => {
      const properties = elementInteractionFromEvent(event);
      if (!properties) return;
      sendCataloguedWebEvent(TelemetryEvent.ELEMENT_INTERACTION, properties, {
        path: window.location.pathname,
        cid,
        endpoint,
      });
    };

    document.addEventListener("click", captureInteraction, true);
    document.addEventListener("change", captureInteraction, true);
    document.addEventListener("submit", captureInteraction, true);
    return () => {
      document.removeEventListener("click", captureInteraction, true);
      document.removeEventListener("change", captureInteraction, true);
      document.removeEventListener("submit", captureInteraction, true);
    };
  }, [hasTelemetryConsent, sameOrigin]);

  return null;
}

function isPersistedPageTransition(event: Event): boolean {
  return "persisted" in event && Boolean((event as PageTransitionEvent).persisted);
}

function sendCataloguedWebEvent<Name extends WebConversionEventName>(
  eventName: Name,
  properties: AnalyticsEventProperties<Name>,
  context: { path: string; cid: string | null; endpoint: string },
): void {
  try {
    validateAnalyticsEventProperties(eventName, properties);
  } catch {
    console.error("Telemetry capture validation failed", { eventName });
    return;
  }
  sendWebEvent(
    {
      event_id: crypto.randomUUID(),
      type: "conversion",
      timestamp: new Date().toISOString(),
      path: context.path,
      event_name: eventName,
      cid: context.cid,
      properties,
      ...browserMarkers(),
    },
    context.endpoint,
  );
}
